<template>
  <div class="flex flex-col min-h-screen">
    <!-- Main container with spacing -->
    <div class="md:flex w-full md:mx-auto relative md:flex-1" :class="{ 'gap-3': !isDashboard }">

      <!-- Left Navigation -->
      <div v-if="isLeftNavEnabled" class="left-nav">
        <LeftNav />
      </div>

      <!-- Right Content Area -->
      <div class="flex flex-col flex-1 right-content">
        <CatalogSearchBar v-if="isLeftNavEnabled" />
        <div class="relative md:flex md:flex-col md:flex-1" :class="{ 'md:pl-2': !isDashboard }">

            <Breadcrumbs v-if="isLeftNavEnabled" />

            <!-- Page content -->
            <div class="page-content">
                <slot></slot>
            </div>


            <!-- NEW: pre-footer slot (where we'll move the sponsor carousel) -->
            <div id="vr-pre-footer-slot" class="px-2 md:px-4"></div>


            <!-- Footer stays under right content -->
            <Footer v-if="isLeftNavEnabled" />
        </div>

      </div>
    </div>

    <div
      v-if="demoFavoritesStore.toastVisible"
      class="fixed top-16 left-1/2 -translate-x-1/2 z-[1052] w-[calc(100%-1rem)] max-w-[500px] flex items-start gap-2 p-3 rounded bg-[#FEE2E2] border border-[#FCA5A5] shadow-md"
      role="alert"
    >
      <i class="ri-error-warning-line text-xl leading-5 text-[#C61E1F]" aria-hidden="true"></i>
      <div>
        <p class="m-0 text-base font-semibold text-[#762123]">{{ $t('favorites.demoUnavailable.title') }}</p>
        <p class="m-0 text-sm text-[#762123]">{{ $t('favorites.demoUnavailable.message') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onBeforeUnmount } from "vue";
import LeftNav from "@/components/common/LeftNav.vue";
import CatalogSearchBar from "@/components/GlobalCatalog/CatalogSearchBar.vue";
import Breadcrumbs from "@/components/common/Breadcrumbs.vue";
import Footer from "@/components/Footer.vue";
import { useDemoFavoritesStore } from "@/stores/demoFavoritesStore";

const demoFavoritesStore = useDemoFavoritesStore();

const config = inject("config");
const isLeftNavEnabled = computed(() => config?.value?.rhVRLeftNavigationMenuFlag);
const isDashboard = window.location.pathname === '/dashboard'


// Relocation logic (VR-only & dashboard-only)
let mo; // mutation observer

function moveCarouselBeforeFooter() {
  // Only run on VR pages with left nav enabled, and only on dashboard
  if (!isLeftNavEnabled.value || !isDashboard) return false;

  const carouselRoot = document.getElementById("vueSponsorCarousel");
  const preFooterSlot = document.getElementById("vr-pre-footer-slot");

  if (!carouselRoot || !preFooterSlot) return false;

  // If it's already there, do nothing
  if (carouselRoot.parentElement === preFooterSlot) return true;

  // Move the entire existing node into the slot
  try {
    preFooterSlot.appendChild(carouselRoot);
    return true;
  } catch (e) {
    console.warn("Failed to move vueSponsorCarousel before VR footer:", e);
    return false;
  }
}

onMounted(() => {
  if (!isDashboard) return;

  // Attempt immediately (in case both nodes are already in DOM)
  if (moveCarouselBeforeFooter()) return;

  // Observe the DOM and retry when nodes appear (covers async loads)
  mo = new MutationObserver(() => {
    if (moveCarouselBeforeFooter()) {
      mo.disconnect();
      mo = null;
    }
  });

  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Also try once on window load (last resort)
  window.addEventListener("load", moveCarouselBeforeFooter);
});

onBeforeUnmount(() => {
  if (mo) mo.disconnect();
  window.removeEventListener("load", moveCarouselBeforeFooter);
});

</script>

<style scoped>
/* Desktop only */
@media (min-width: 768px) {
  .left-nav {
    height: 100vh;
    /* Full viewport height */
    position: sticky;
    /* Stick to top */
    top: 0;
    flex-shrink: 0;
    /* Prevent shrinking */
  }

  .right-content {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    /* Prevent double scrollbars */
  }

  .page-content {
    flex: 1 1 auto;
    /* Allow natural growth */
  }
}

@media (min-width: 3000px) {
  .page-content {
    flex: none !important;
  }
}
</style>
